import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AngularMaterialModule } from '../../../../../angular-material.module';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { TemplateComponent } from '../../../../../common/template/template.component';
import { EditRequestComponent } from '../../edit-request/edit-request.component';
import { CommentComponent } from '../../comment/comment.component';
import { AdddPhotoComponent } from '../../addd-photo/addd-photo.component';
import { environment } from '../../../../../../environments/environment';
import { BreadcrumbComponent } from '../../../../../common/breadcrumb/breadcrumb/breadcrumb.component';
import {
  CustomTableViewComponent,
  TableColumn,
} from '../../../../../common/custom-table-view/custom-table-view.component';
import { RouterModule } from '@angular/router';
import { RaiseRequestService } from '../../../../../services/raise-request.service';
import { snackBarService } from '../../../../../services/snack-bar.service';
import { ApiService } from '../../../../../services/api.service';
import { getAccountGroupIdFromSession } from '../../../../../Modules/assets/asset-movement/asset-movement.utils';
import { UpdateSatatusComponent } from '../../update-satatus/update-satatus.component';
import { EMPTY, Observable, Subject } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { RaiseRequestsComponent } from '../raise-requests.component';
import * as XLSX from 'xlsx';

declare const bootstrap: any;

interface Issue {
  issue_id: number;
  issue_photo?: string;
  issuePhotos?: string[];
  status_name?: string;
  status?: number | string;
  account_id?: number | string;
  account_name?: string;
  priority_id?: number | string;
  priority_name?: string;
  issue_category_id?: number | string;
  category_name?: string;
  sub_category_name?: string;
  block?: string;
  floor_unit?: string;
  reported_name?: string;
  created_at?: string | Date;
  description?: string;
  assigned_to_name?: string;
  assigned_to?: number | string;
  created_by?: number | string;
  latest_comment?: string;
  commented_by?: string;
  commented_at?: string | Date;
}

interface StatusOption {
  status_id: number | string;
  status: string;
}

interface CategoryOption {
  issue_category_id: number | string;
  issue_category: string;
}

interface SubCategoryOption {
  issue_category_id: number | string;
  issue_category: string;
}

interface WingOption {
  block_id: number | string;
  block_name: string;
}

interface FloorOption {
  floor_id: number | string;
  floor_name: string;
}

interface UnitTypeOption {
  unit_type: string;
}

interface UnitOption {
  floor_unit_id: number | string;
  floor_unit: string;
  block_id?: number | string;
  unit_type?: string;
}

interface AccountOption {
  account_id: number | string;
  project_name: string;
}

interface PriorityOption {
  priority_id?: number | string;
  id?: number | string;
  priority?: string;
  name?: string;
}

interface UserProfile {
  user_id: number | string;
  role: string;
  role_id?: number | string;
  [key: string]: unknown;
}

interface FilterCriteria {
  account: string;
  category: string;
  subCategory: string;
  status: string;
  priority: string;
  wing: string;
  floor: string;
  unitType: string;
  unit: string;
}

@Component({
  selector: 'app-my-issues-requests',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    CommonModule,
    TemplateComponent,
    BreadcrumbComponent,
    RouterModule,
    CustomTableViewComponent,
  ],
  templateUrl: './my-issues-requests.component.html',
  styleUrls: ['../all-requests/all-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyIssuesRequestsComponent implements OnInit, OnDestroy {
  readonly tableColumns: TableColumn[] = [
    { key: 'slno', title: 'Sl No.', sortable: false, width: 70, filterable: false },
    { key: 'category_name', title: 'Category', sortable: true },
    {
      key: 'created_at',
      title: 'Date',
      sortable: true,
      format: (value) =>
        value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
    },
    { key: 'block', title: 'Wing', sortable: true },
    { key: 'floor_unit', title: 'Unit', sortable: true },
    { key: 'priority_name', title: 'Priority', sortable: true },
    { key: 'status_name', title: 'Status', sortable: true },
    { key: 'account_name', title: 'Project', sortable: true },
    { key: 'reported_name', title: 'Raised By', sortable: true },
    { key: 'assigned_to_name', title: 'Assigned To', sortable: true },
    { key: 'description', title: 'Description', sortable: false, width: 220 },
  ];

  private readonly raiseRequestService = inject(RaiseRequestService);
  private readonly snackBarService = inject(snackBarService);
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  readonly baseUrl = environment.baseUrl;
  readonly storageUrl = environment.storageUrl;

  private readonly accountID = sessionStorage.getItem('account_id') || '';
  private readonly userID = sessionStorage.getItem('session_id') || '';
  private readonly roleID = sessionStorage.getItem('role_id') || '';

  readonly loading = signal<boolean>(false);
  readonly activeTab = signal<'raised' | 'assigned'>('raised');
  readonly viewMode = signal<'card' | 'list'>('card');
  readonly cardItemsPerPage = signal<number>(50);
  readonly cardCurrentPage = signal<number>(1);
  readonly totalCount = signal<number>(0);
  readonly filteredCount = signal<number>(0);

  readonly displayFilteredCount = computed(() => {
    const fCount = this.filteredCount();
    if (fCount !== null && fCount !== undefined && !Number.isNaN(fCount) && fCount > 0) {
      return fCount;
    }
    return this.currentRequests().length;
  });

  filterForm!: FormGroup;

  readonly allRequestList = signal<Issue[]>([]);
  readonly categoryList = signal<CategoryOption[]>([]);
  readonly subCategoryList = signal<SubCategoryOption[]>([]);
  readonly showSubCategoryFilter = signal(false);
  readonly wingList = signal<WingOption[]>([]);
  readonly floorList = signal<FloorOption[]>([]);
  readonly unitTypeList = signal<UnitTypeOption[]>([]);
  readonly unitList = signal<UnitOption[]>([]);
  readonly statusList = signal<StatusOption[]>([]);
  readonly accountList = signal<AccountOption[]>([]);
  readonly priorityList = signal<PriorityOption[]>([]);
  readonly userProfile = signal<UserProfile | null>(null);

  readonly selectedCategory = signal<string>('');
  readonly selectedSubCategory = signal<string>('');
  readonly selectedStatus = signal<string>('');
  readonly selectedAccount = signal<string>(this.accountID);
  readonly selectedPriority = signal<string>('');
  readonly selectedWing = signal<string>('');
  readonly selectedFloor = signal<string>('');
  readonly selectedUnitType = signal<string>('');
  readonly selectedUnit = signal<string>('');

  private carouselInstances: Map<HTMLElement, any> = new Map();
  private readonly destroy$ = new Subject<void>();
  private readonly carouselInitTimeout = signal<number | null>(null);

  // Role checks based on reference table: Superadmin (1), Builder Admin (12), Staff (3, etc.)
  readonly userRoleId = computed(() => {
    const profile = this.userProfile();
    const idFromProfile = profile?.role_id != null ? Number(profile.role_id) : NaN;
    if (!Number.isNaN(idFromProfile)) {
      return idFromProfile;
    }
    const idFromSession = parseInt(this.roleID || '0', 10);
    return Number.isNaN(idFromSession) ? 0 : idFromSession;
  });

  readonly userRoleName = computed(() => {
    return String(this.userProfile()?.role || '').trim();
  });

  readonly isSuperAdmin = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    return roleId === 1 || roleName === 'Superadmin';
  });

  readonly isBuilderAdmin = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    return roleId === 12 || roleName === 'Builder Admin';
  });

  readonly isStaffUser = computed(() => {
    const roleId = this.userRoleId();
    const roleName = this.userRoleName();
    const staffRoleIds = [3, 13, 14, 15, 16, 17, 18, 19];
    const staffRoleNames = [
      'Staff', 'Builder Staff', 'Security Guard', 'Builder Manager',
      'Asset Manager', 'Checklist Inspector', 'Engineer', 'Project Manager', 'PM/APM/SE'
    ];
    return (
      staffRoleIds.includes(roleId) ||
      staffRoleNames.includes(roleName) ||
      (!this.isSuperAdmin() && !this.isBuilderAdmin())
    );
  });

  // Getter for template backwards compatibility
  get isBuilder(): boolean {
    return this.isBuilderAdmin();
  }

  // Edit Request access: Superadmin (role_id 1) and Builder Admin (role_id 12)
  canEditRequest(_issue?: Issue): boolean {
    return this.isSuperAdmin() || this.isBuilderAdmin();
  }

  private isResolvedOrDeferred(issue?: Issue): boolean {
    if (!issue) {
      return false;
    }
    const statusNum = Number(issue.status);
    const statusName = String(issue.status_name || '').toLowerCase().trim();
    return (
      statusNum === 3 ||
      statusName === 'resolved' ||
      statusName === 'deferred' ||
      statusName === 'deffered' ||
      statusName.includes('resolve') ||
      statusName.includes('defer')
    );
  }

  // Edit Status access: Superadmin, Builder Admin can update always; Staff can update unless status is Resolved or Deferred
  canUpdateStatus(issue?: Issue): boolean {
    if (this.isSuperAdmin() || this.isBuilderAdmin()) {
      return true;
    }
    if (this.isResolvedOrDeferred(issue)) {
      return false;
    }
    if (this.isStaffUser()) {
      return true;
    }
    if (!issue) {
      return true;
    }
    const currentUserId = this.userProfile()?.user_id || parseInt(this.userID || '0', 10);
    return (
      String(issue.assigned_to) === String(currentUserId) ||
      String(issue.created_by) === String(currentUserId)
    );
  }

  readonly filterCriteria = computed<FilterCriteria>(() => ({
    account: this.selectedAccount(),
    category: this.selectedCategory(),
    subCategory: this.selectedSubCategory(),
    status: this.selectedStatus(),
    priority: this.selectedPriority(),
    wing: this.selectedWing(),
    floor: this.selectedFloor(),
    unitType: this.selectedUnitType(),
    unit: this.selectedUnit(),
  }));

  readonly currentRequests = computed(() => {
    const criteria = this.filterCriteria();
    return this.applyFilters(this.allRequestList(), criteria, true);
  });

  readonly requestCount = computed(() => {
    const filtered = this.displayFilteredCount();
    const total = this.totalCount();
    if (total > 0) {
      return `${filtered} of ${total}`;
    }
    return `${filtered}`;
  });

  readonly tableData = computed(() => {
    const offset = (this.cardCurrentPage() - 1) * this.cardItemsPerPage();
    return this.currentRequests().map((issue, index) => ({
      ...issue,
      slno: offset + index + 1,
    }));
  });

  private applyFilters(
    requests: Issue[],
    criteria: FilterCriteria,
    includePriority: boolean
  ): Issue[] {
    return requests.filter((issue) => {
      const accountMatch = criteria.account
        ? String(issue.account_id) === String(criteria.account)
        : true;
      const categoryMatch = criteria.category
        ? String(issue.issue_category_id) === String(criteria.category)
        : true;
      const statusMatch = criteria.status
        ? String(issue.status) === String(criteria.status)
        : true;
      const priorityMatch = includePriority
        ? criteria.priority
          ? String(issue.priority_id) === String(criteria.priority)
          : true
        : true;

      return accountMatch && categoryMatch && statusMatch && priorityMatch;
    });
  }

  constructor(private readonly fb: FormBuilder) {
    effect(() => {
      const profile = this.userProfile();
      if (profile && this.accountList().length === 0) {
        this.fetchAccounts();
      }
    });
  }

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      account: [this.selectedAccount()],
      status: [this.selectedStatus()],
      category: [this.selectedCategory()],
      sub_category: [this.selectedSubCategory()],
      priority: [this.selectedPriority()],
      wing: [this.selectedWing()],
      floor: [this.selectedFloor()],
      unit_type: [this.selectedUnitType()],
      unit: [this.selectedUnit()],
    });

    this.setupFilterFormListeners();
    this.fetchMyIssues();
    this.fetchUserProfile();
    this.allCategoryDropdown();
    this.fetchAllStatus();
    this.fetchAllPriority();
    this.loadLocationFilterDropdowns(this.selectedAccount());
    this.fetchAccounts();
  }

  /** 🔹 Raise Request Dialog */
  raiseRequests(): void {
    this.dialog
      .open(RaiseRequestsComponent, {
        autoFocus: false,
        width: '760px',
        maxWidth: '95vw',
        panelClass: 'submit-ticket-dialog',
      })
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) {
          this.fetchMyIssues();
        }
      });
  }

  private setupFilterFormListeners(): void {
    this.filterForm.get('account')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountId) => {
        this.resetDependentFilters(['wing', 'floor', 'unit_type', 'unit']);
        this.floorList.set([]);
        this.unitList.set([]);
        if (accountId) {
          this.loadLocationFilterDropdowns(accountId);
        } else {
          this.wingList.set([]);
          this.floorList.set([]);
          this.unitTypeList.set([]);
        }
      });

    this.filterForm.get('category')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((categoryId) => {
        this.resetDependentFilters(['sub_category']);
        if (categoryId) {
          this.loadSubCategoryDropdown(categoryId);
        } else {
          this.subCategoryList.set([]);
          this.showSubCategoryFilter.set(false);
        }
      });

    this.filterForm.get('wing')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['floor', 'unit']);
        this.refreshFloorDropdown();
        this.refreshUnitDropdown();
      });

    this.filterForm.get('floor')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['unit']);
        this.refreshUnitDropdown();
      });

    this.filterForm.get('unit_type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetDependentFilters(['unit']);
        this.refreshUnitDropdown();
      });
  }

  private resetDependentFilters(fields: string[]): void {
    const patch: Record<string, string> = {};
    fields.forEach((field) => {
      patch[field] = '';
    });
    this.filterForm.patchValue(patch, { emitEvent: false });
  }

  private getActiveAccountId(): string {
    return (
      this.filterForm?.get('account')?.value ||
      this.selectedAccount() ||
      this.accountID ||
      ''
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupCarousels();
    const timeoutId = this.carouselInitTimeout();
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }

  onTabChange(tab: 'raised' | 'assigned'): void {
    if (this.activeTab() === tab) {
      return;
    }
    this.activeTab.set(tab);
    this.cardCurrentPage.set(1);
    this.fetchMyIssues();
  }

  onViewToggle(isListView: boolean): void {
    this.viewMode.set(isListView ? 'list' : 'card');
  }

  cardPreviousPage(): void {
    if (this.cardHasPreviousPage()) {
      this.cardCurrentPage.update((p) => p - 1);
      this.fetchMyIssues();
    }
  }

  cardNextPage(): void {
    if (this.cardHasNextPage()) {
      this.cardCurrentPage.update((p) => p + 1);
      this.fetchMyIssues();
    }
  }

  onCardItemsPerPageChange(size: number): void {
    this.cardItemsPerPage.set(size);
    this.cardCurrentPage.set(1);
    this.fetchMyIssues();
  }

  onTablePageChange(event: { pageIndex: number; pageSize: number; offset: number }): void {
    this.cardItemsPerPage.set(event.pageSize);
    this.cardCurrentPage.set(event.pageIndex + 1);
    this.fetchMyIssues();
  }

  readonly paginatedCards = computed(() => {
    return this.currentRequests();
  });

  readonly cardPageRangeStart = computed(() => {
    const total = this.displayFilteredCount();
    if (total === 0) {
      return 0;
    }
    return (this.cardCurrentPage() - 1) * this.cardItemsPerPage() + 1;
  });

  readonly cardPageRangeEnd = computed(() => {
    const total = this.displayFilteredCount();
    return Math.min(
      this.cardCurrentPage() * this.cardItemsPerPage(),
      total
    );
  });

  readonly cardHasPreviousPage = computed(() => this.cardCurrentPage() > 1);

  readonly cardHasNextPage = computed(() => {
    const total = this.displayFilteredCount();
    if (total > 0) {
      return this.cardCurrentPage() * this.cardItemsPerPage() < total;
    }
    return this.allRequestList().length >= this.cardItemsPerPage();
  });

  onApplyFilter(): void {
    if (!this.filterForm) {
      return;
    }

    const {
      account,
      status,
      category,
      sub_category,
      priority,
      wing,
      floor,
      unit_type,
      unit,
    } = this.filterForm.value;

    this.selectedAccount.set(account ?? '');
    this.selectedStatus.set(status ?? '');
    this.selectedCategory.set(category ?? '');
    this.selectedSubCategory.set(sub_category ?? '');
    this.selectedPriority.set(priority ?? '');
    this.selectedWing.set(wing ?? '');
    this.selectedFloor.set(floor ?? '');
    this.selectedUnitType.set(unit_type ?? '');
    this.selectedUnit.set(unit ?? '');

    this.fetchMyIssues();
  }

  private getImageUrl(issue: Issue): string {
    const photos = issue.issuePhotos && issue.issuePhotos.length > 0
      ? issue.issuePhotos
      : issue.issue_photo
        ? issue.issue_photo.split(',').filter(Boolean)
        : [];

    if (!photos.length) {
      return '';
    }

    const cleanStorageUrl = this.storageUrl ? this.storageUrl.replace(/\/$/, '') : '';
    return photos
      .map((p) => {
        const cleanPhoto = p.trim().replace(/^\//, '');
        return cleanStorageUrl ? `${cleanStorageUrl}/${cleanPhoto}` : cleanPhoto;
      })
      .join(', ');
  }

  exportTableToExcel(data: Issue[], fileName = 'my-requests.xlsx'): void {
    const exportData = (data || []).map((issue, index) => ({
      'Sl No.': index + 1,
      'Category': issue.category_name || '',
      'Sub Category': issue.sub_category_name || '',
      'Date': issue.created_at
        ? new Date(issue.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : '',
      'Wing': issue.block || '',
      'Unit': issue.floor_unit || '',
      'Priority': issue.priority_name || '',
      'Status': issue.status_name || '',
      'Project': issue.account_name || '',
      'Raised By': issue.reported_name || '',
      'Assigned To': issue.assigned_to_name || '',
      'Description': issue.description || '',
      'Image': this.getImageUrl(issue),
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'My Requests');
    XLSX.writeFile(wb, fileName);
  }

  private createHttpObservable<T>(
    observable: Observable<T>,
    errorMessage: string
  ): Observable<T> {
    return observable.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((error) => {
        console.error(errorMessage, error);
        this.handleError(error, errorMessage);
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    );
  }

  fetchAccounts(): void {
    const payload = { user_id: parseInt(this.userID || '0', 10) };
    const request$ = this.http.post<{ data: AccountOption[] }>(
      `${this.baseUrl}/assign_project_dropdown`,
      payload
    );

    this.createHttpObservable(request$, 'Failed to fetch accounts')
      .pipe(
        map((res) => res?.data || []),
        tap((accounts) => this.accountList.set(accounts))
      )
      .subscribe();
  }

  fetchAllPriority(): void {
    const request$ = this.http.get<PriorityOption[]>(
      `${this.baseUrl}/priority_dropdown`
    );

    this.createHttpObservable(request$, 'Failed to fetch priorities')
      .pipe(tap((priorities) => this.priorityList.set(priorities || [])))
      .subscribe();
  }

  fetchAllStatus(): void {
    const request$ = this.http.get<StatusOption[]>(
      `${this.baseUrl}/status_dropdown`
    );

    this.createHttpObservable(request$, 'Failed to fetch statuses')
      .pipe(tap((statuses) => this.statusList.set(statuses || [])))
      .subscribe();
  }

  allCategoryDropdown(): void {
    const accountGroupId = getAccountGroupIdFromSession();
    if (accountGroupId == null) {
      this.categoryList.set([]);
      return;
    }

    const request$ = this.raiseRequestService.getCategories({
      account_group_id: accountGroupId,
    });

    this.createHttpObservable(request$, 'Failed to fetch categories')
      .pipe(
        map((res: any) => this.unwrapDropdownList(res)),
        tap((categories) => this.categoryList.set(categories || []))
      )
      .subscribe();
  }

  loadSubCategoryDropdown(categoryId?: string | number): void {
    const categoryIdToUse =
      categoryId || this.filterForm?.get('category')?.value;

    if (!categoryIdToUse) {
      this.subCategoryList.set([]);
      this.showSubCategoryFilter.set(false);
      return;
    }

    const request$ = this.raiseRequestService.getSubCategories({
      issue_category_id: categoryIdToUse,
    });

    this.createHttpObservable(request$, 'Failed to fetch sub categories')
      .pipe(
        map((res: any) =>
          this.normalizeSubCategories(this.unwrapDropdownList(res))
        ),
        tap((subCategories) => {
          this.subCategoryList.set(subCategories);
          this.showSubCategoryFilter.set(subCategories.length > 0);
          if (subCategories.length === 0) {
            this.filterForm.patchValue({ sub_category: '' }, { emitEvent: false });
          }
        })
      )
      .subscribe();
  }

  loadLocationFilterDropdowns(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();

    if (!accountIdToUse) {
      this.wingList.set([]);
      this.floorList.set([]);
      this.unitTypeList.set([]);
      this.unitList.set([]);
      return;
    }

    const wings$ = this.raiseRequestService.getBlockDropdown(accountIdToUse);
    const unitTypes$ =
      this.raiseRequestService.getUnitTypeDropdown(accountIdToUse);

    this.createHttpObservable(wings$, 'Failed to fetch wings')
      .pipe(
        map((res: any) => this.normalizeWings(this.unwrapDropdownList(res))),
        tap((wings) => this.wingList.set(wings))
      )
      .subscribe();

    this.createHttpObservable(unitTypes$, 'Failed to fetch unit types')
      .pipe(
        map((res: any) => this.normalizeUnitTypes(this.unwrapDropdownList(res))),
        tap((unitTypes) => this.unitTypeList.set(unitTypes))
      )
      .subscribe();

    this.refreshFloorDropdown(accountIdToUse);
    this.refreshUnitDropdown(accountIdToUse);
  }

  refreshFloorDropdown(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();
    const wingId = this.filterForm?.get('wing')?.value;

    if (!accountIdToUse || !wingId) {
      this.floorList.set([]);
      return;
    }

    const request$ = this.raiseRequestService.getFloorDropdown(
      accountIdToUse,
      wingId
    );

    this.createHttpObservable(request$, 'Failed to fetch floors')
      .pipe(
        map((res: any) => this.normalizeFloors(this.unwrapDropdownList(res))),
        tap((floors) => this.floorList.set(floors))
      )
      .subscribe();
  }

  refreshUnitDropdown(accountId?: string | number): void {
    const accountIdToUse = accountId || this.getActiveAccountId();
    const wingId = this.filterForm?.get('wing')?.value;
    const floorId = this.filterForm?.get('floor')?.value;
    const unitType = this.filterForm?.get('unit_type')?.value;

    if (!accountIdToUse) {
      this.unitList.set([]);
      return;
    }

    const payload = {
      account_id: accountIdToUse,
      block_id: wingId || null,
      phase_id: null,
      floor_id: floorId || null,
      unit_type: unitType || null,
    };

    const request$ = this.raiseRequestService.getUnitDropdown(payload);

    this.createHttpObservable(request$, 'Failed to fetch units')
      .pipe(
        map((res: any) => this.normalizeUnits(this.unwrapDropdownList(res))),
        tap((units) => this.unitList.set(units))
      )
      .subscribe();
  }

  fetchMyIssues(): void {
    this.loading.set(true);
    const criteria = this.filterCriteria();
    const userId = parseInt(this.userID || '0', 10);
    const accountId = parseInt(criteria.account || this.accountID || '0', 10);
    const limit = this.cardItemsPerPage() || 50;
    const offset = (this.cardCurrentPage() - 1) * limit;

    const payload = {
      account_id: accountId,
      user_id: userId,
      status: this.toNullableFilterValue(criteria.status),
      priority_id: this.toNullableFilterValue(criteria.priority),
      floor_unit_id: this.toNullableFilterValue(criteria.unit),
      issue_category_id: this.toNullableFilterValue(criteria.category),
      issue_sub_category_id: this.toNullableFilterValue(criteria.subCategory),
      block_id: this.toNullableFilterValue(criteria.wing),
      floor_id: this.toNullableFilterValue(criteria.floor),
      unit_type: criteria.unitType || null,
      limit: limit,
      offset: offset,
      ...(this.activeTab() === 'assigned' ? { assigned_to: userId } : {}),
    };

    this.raiseRequestService
      .fetchMyIssues(payload)
      .pipe(
        map((res: any) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          const filtered = res?.filtered_count ?? res?.filteredCount ?? res?.count ?? (Array.isArray(res) ? res.length : 0);
          const total = res?.total_count ?? res?.totalCount ?? res?.total_records ?? res?.overall_total ?? filtered;
          this.filteredCount.set(filtered);
          this.totalCount.set(total);
          return (list || []).map((issue: Issue) => ({
            ...issue,
            issuePhotos: issue.issue_photo
              ? issue.issue_photo.split(',').filter(Boolean)
              : [],
          }));
        }),
        tap((issues) => {
          this.allRequestList.set(issues);
          this.startCarouselAutoSlide();
        }),
        catchError((error) => {
          console.error('Error fetching my issues:', error);
          this.snackBarService.showError(
            'Failed to fetch issues, please try again later.'
          );
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  fetchUserProfile(): void {
    if (!this.userID) {
      return;
    }

    this.apiService
      .fetchUserProfile(this.userID)
      .pipe(
        tap((profile: UserProfile) => this.userProfile.set(profile)),
        catchError((error) => {
          console.error('Error fetching user profile:', error);
          this.snackBarService.showError(
            'Failed to fetch user profile, please try again later.'
          );
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private startCarouselAutoSlide(): void {
    const timeoutId = this.carouselInitTimeout();
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = window.setTimeout(() => {
      this.initializeCarousels();
    }, 0);

    this.carouselInitTimeout.set(newTimeoutId);
  }

  private initializeCarousels(): void {
    const carouselElements = document.querySelectorAll<HTMLElement>(
      '.issue-card .carousel'
    );

    carouselElements.forEach((carouselEl) => {
      const existingInstance = bootstrap.Carousel.getInstance(carouselEl);
      if (existingInstance) {
        existingInstance.dispose();
        this.carouselInstances.delete(carouselEl);
      }

      try {
        const instance = new bootstrap.Carousel(carouselEl, {
          interval: 4000,
          ride: 'carousel',
          pause: false,
        });
        instance.cycle();
        this.carouselInstances.set(carouselEl, instance);
      } catch (error) {
        console.warn('Failed to initialize carousel:', error);
      }
    });
  }

  private cleanupCarousels(): void {
    this.carouselInstances.forEach((instance) => {
      try {
        instance.dispose();
      } catch (error) {
        console.warn('Error disposing carousel:', error);
      }
    });
    this.carouselInstances.clear();
  }

  openAssignToDialog(item: Issue): void {
    const dialogRef = this.dialog.open(EditRequestComponent, {
      width: '706px',
      maxHeight: '90vh',
      data: { item, for: 'issue' },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchMyIssues();
        }
      });
  }

  openEditDialog(item: Issue): void {
    const dialogRef = this.dialog.open(EditRequestComponent, {
      width: '706px',
      maxHeight: '90vh',
      data: { item, for: 'issue' },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchMyIssues();
        }
      });
  }

  openComments(issueId: number): void {
    this.dialog.open(CommentComponent, {
      width: '560px',
      maxHeight: '90vh',
      data: { issueId },
    });
  }

  openAddIuusePhoto(issueId: number): void {
    const dialogRef = this.dialog.open(AdddPhotoComponent, {
      width: '560px',
      maxHeight: '90vh',
      data: { issueId },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchMyIssues();
        }
      });
  }

  openUpdateStatus(issue: Issue): void {
    const dialogRef = this.dialog.open(UpdateSatatusComponent, {
      width: '500px',
      data: { item: issue },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.fetchMyIssues();
        }
      });
  }

  private toNullableFilterValue(value: string): number | null {
    if (!value) {
      return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private unwrapDropdownList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    return [];
  }

  private normalizeSubCategories(items: any[]): SubCategoryOption[] {
    return items.map((item) => ({
      issue_category_id:
        item.issue_category_id ??
        item.issue_sub_category_id ??
        item.sub_category_id ??
        item.id,
      issue_category:
        item.issue_category ??
        item.issue_sub_category ??
        item.sub_category ??
        item.name ??
        '',
    }));
  }

  private normalizeWings(items: any[]): WingOption[] {
    return items.map((item) => ({
      block_id: item.block_id ?? item.wing_id ?? item.id,
      block_name: item.block_name ?? item.wing_name ?? item.name ?? '',
    }));
  }

  private normalizeFloors(items: any[]): FloorOption[] {
    return items.map((item) => ({
      floor_id: item.floor_id ?? item.id,
      floor_name: item.floor_name ?? item.name ?? '',
    }));
  }

  private normalizeUnitTypes(items: any[]): UnitTypeOption[] {
    return items.map((item) => ({
      unit_type: item.unit_type ?? item.unit_type_name ?? item.name ?? '',
    }));
  }

  private normalizeUnits(items: any[]): UnitOption[] {
    return items.map((item) => ({
      floor_unit_id: item.floor_unit_id ?? item.unit_id ?? item.id,
      floor_unit: item.floor_unit ?? item.unit_name ?? item.name ?? '',
      block_id: item.block_id,
      unit_type: item.unit_type,
    }));
  }

  private handleError(error: unknown, message: string): void {
    console.error(message, error);
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  trackByIssueId(_index: number, issue: Issue): number {
    return issue.issue_id;
  }

  get allUserprofileList(): UserProfile | null {
    return this.userProfile();
  }
}
